/**
 * Platform factory — auto-detects the runtime environment and returns
 * the appropriate platform adapter.
 *
 * Usage in any React component/service:
 *   import { platform } from '@platform/index';
 *   const deviceId = await platform.getDeviceId();
 *   if (platform.supportsAmiBroker) { ... }
 *
 * Detection order:
 * 1. Capacitor globals present → MobilePlatform (checked first — native takes priority)
 * 2. Tauri globals present → DesktopPlatform
 * 3. Default → WebPlatform
 */
import { PlatformAdapter, PlatformType } from "./platform.interface";
import { DesktopPlatform } from "./tauri.platform";
import { WebPlatform } from "./web.platform";
import { MobilePlatform } from "./capacitor.platform";

function detectPlatform(): PlatformType {
  // Check for a real Capacitor native runtime FIRST.
  // Some web contexts may expose a `window.Capacitor` object (stubs/extensions),
  // which should NOT force the mobile UI.
  if (typeof window !== "undefined") {
    const cap = (window as any).Capacitor;
    const isNativeByApi = typeof cap?.isNativePlatform === "function" && cap.isNativePlatform();
    const platform = typeof cap?.getPlatform === "function" ? cap.getPlatform() : undefined;
    const isNativeByPlatform = platform === "android" || platform === "ios";
    const hasNativeAppPlugin = typeof cap?.Plugins?.App?.addListener === "function";

    if (isNativeByApi || isNativeByPlatform || hasNativeAppPlugin) {
      return "mobile";
    }
  }

  // Check for Tauri v2 runtime
  if (
    typeof window !== "undefined" &&
    (window as any).__TAURI_INTERNALS__ !== undefined
  ) {
    return "desktop";
  }

  // Default to web
  return "web";
}

function createPlatform(type: PlatformType): PlatformAdapter {
  switch (type) {
    case "desktop":
      return new DesktopPlatform();
    case "mobile":
      return new MobilePlatform();
    case "web":
    default:
      return new WebPlatform();
  }
}

/** The detected platform type. */
export const platformType: PlatformType = detectPlatform();

/** The singleton platform adapter instance for the current runtime. */
export const platform: PlatformAdapter = createPlatform(platformType);

/** Check helpers for conditional rendering. */
export const isDesktop = platformType === "desktop";
export const isWeb = platformType === "web";
export const isMobile = platformType === "mobile";

// Re-export types
export type { PlatformAdapter, PlatformType } from "./platform.interface";
